/* eslint-disable @angular-eslint/component-class-suffix */
import {
  Component,
  ChangeDetectionStrategy,
  AfterViewInit,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { ChangeDetectorRef, OnInit } from '@angular/core';
import { countdown, Countdown } from '@shared/utils';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '../../services/authentication.service';
import { finalize } from 'rxjs';
import { DialogService } from '@migo-apps/dialogs';
import { TranslateService } from '@ngx-translate/core';
import { InputComponent } from '@migo-apps/migo-uikit-v4';
import { ToastService } from '@migo-apps/components';
import { BreakpointService } from '../../../shared/services/breakpoint/breakpoint.service';

const SOMETHING_WRONG_TITLE = 'COMMON.SOMETHING-WRONG-TITLE';
const SOMETHING_WRONG_MESSAGE = 'COMMON.SOMETHING-WRONG-MESSAGE';
const INPUT_CODE = 'AUTH.DIALOG.OTP.INPUT-CODE';
const INVALID_CODE = 'AUTH.DIALOG.OTP.INVALID-CODE';
const CODE_RESEND_TITLE = 'AUTH.DIALOG.OTP.CODE-RESEND-TITLE';
const CODE_RESEND_MESSAGE = 'AUTH.DIALOG.OTP.CODE-RESEND-MESSAGE';

/**
 *Pagina para validar un codigo OTP
 *
 * @export
 * @class ValidateOtpPage
 * @implements {OnInit}
 * @implements {AfterViewInit}
 */
@Component({
  standalone: false,
  selector: 'migo-apps-validate-otp',
  templateUrl: './validate-otp.page.html',
  styleUrls: ['./validate-otp.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValidateOtpPage implements OnInit, AfterViewInit {
  @ViewChildren(InputComponent) inputs!: QueryList<InputComponent>;
  code: string[] = ['', '', '', '', '', ''];
  countdown!: Countdown;
  email!: string;

  loading = false;
  isErrorCode = false;

  constructor(
    private cd: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private authSvc: AuthenticationService,
    private dialogSvc: DialogService,
    private i18n: TranslateService,
    private toastService: ToastService,
    public bp: BreakpointService
  ) {}

  ngOnInit(): void {
    this.email = this.route.snapshot.queryParamMap.get('email') as string;
    this.toastService.toast(
      `Se envió un código de verificación a tu<br />correo ${this.email}`
    );
    this.cd.markForCheck();
  }

  ngAfterViewInit(): void {
    this.showCountdown(1);
  }

  /**
   * If the code is invalid, show an error message. Otherwise, send the code to the
   * server and navigate to the new password page if the code is valid
   * @returns The observable is being returned.
   */
  onSubmit() {
    this.loading = true;

    this.authSvc
      .validateOTP(this.getCode(), this.email)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cd.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.router.navigate(['auth', 'new-password'], {
            queryParams: { otp: this.getCode() },
            queryParamsHandling: 'merge',
          });
        },
        error: () => {
          this.isErrorCode = true;
        },
      });
  }

  /**
   * It sends a password reset request to the Authentication service
   */
  onRequestCode() {
    this.isErrorCode = false;
    this.authSvc
      .sendPasswordResetRequest(this.email)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cd.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.toastService.toast(
            `Se envió un nuevo código a tu correo<br /> ${this.email}`
          );
        },
        error: () => {
          this.i18n
            .get([SOMETHING_WRONG_TITLE, SOMETHING_WRONG_MESSAGE])
            .subscribe((value) => {
              this.dialogSvc.openInformationDialog({
                title: value[SOMETHING_WRONG_TITLE],
                message: value[SOMETHING_WRONG_MESSAGE],
              });
            });
        },
      });
  }

  /**
   *Crea una nueva subscripcion a una cuenta regresiva
   *
   * @private
   * @param {number} minutes
   * @memberof ValidateOtpPage
   */
  private showCountdown(minutes: number) {
    countdown(minutes).subscribe((countdown) => {
      this.countdown = countdown;
      this.cd.markForCheck();
    });
  }

  onInputChange(index: number, event: any): void {
    this.isErrorCode = false;
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value.length === 1) {
      this.code[index] = value;

      if (index < this.inputs.length - 1) {
        setTimeout(() => {
          const nextInput = this.inputs.toArray()[index + 1];
          if (nextInput && nextInput.nativeElement) {
            nextInput.nativeElement.focus();
          }
        }, 0);
      }
    } else {
      this.code[index] = '';
    }
  }

  onPaste(index: number, event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') ?? '';
    const chars = text.replace(/\s/g, '').split('');
    const inputsArray = this.inputs.toArray();

    chars.forEach((char, i) => {
      const pos = index + i;
      if (pos < this.code.length) {
        this.code[pos] = char;
        inputsArray[pos].nativeElement.value = char;
      }
    });

    const lastPos = Math.min(index + chars.length, this.code.length - 1);
    inputsArray[lastPos].nativeElement.focus();
    this.cd.markForCheck();
  }

  onKeydown(index: number, event: KeyboardEvent): void {
    if (event.key !== 'Backspace' || index === 0) return;
    const input = event.target as HTMLInputElement;
    if (input.value === '') {
      event.preventDefault();
      const prevInput = this.inputs.toArray()[index - 1];
      this.code[index - 1] = '';
      prevInput.nativeElement.value = '';
      prevInput.nativeElement.focus();
      this.cd.markForCheck();
    }
  }

  getCode(): string {
    return this.code.join('').toUpperCase();
  }

  isFullCode() : boolean {
    return this.code.every((str: string) => str !== '');
  }
}
